/* eslint-disable no-mixed-operators */
import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { colors, TextInput, Button } from 'kangarootime-ui';
import { Paper } from '@material-ui/core';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { StylesProvider, createGenerateClassName } from '@material-ui/styles';
import moment from 'moment';
import MaterialTable from 'material-table';
import { orderBy } from 'lodash';
import TimeOffRequestDialog from '../../../../components/TimeOffRequestDialog';
import { flatten } from '../../../../utils/array';
import { numberOfWorkingDays } from '../../../../utils/timeOffRequests';

// installed from material-table
import { CsvBuilder } from 'filefy';

// prevent further clashnames between material-ui and material-table
const generateClassName = createGenerateClassName({
  productionPrefix: 'mt',
  seed: 'mt'
});

const TimeOffOverview = ({
  employeesById,
  timeOffRequests,
  updateTimeOffRequestStatus,
  editRequest,
  currentCenterId,
  archiveRequest,
  isLoading,
  // these are passed as props and kept in the giant container's state so that the dates are still visible in the filter when navigating to any of the other tabs
  searchTimeframeStart,
  searchTimeframeEnd,
  setTimeOffRequestSearchTimeframe,
  searchForRequestsInTimeframe,
  showManagerEmployeeTimeOffRequestDialog
}) => {
  // keeps track of which request we're updating PTO hours for
  const [editsToExistingRequest, setEditsToExistingRequest] = useState({ id: null, hoursApproved: null });
  const [showTimeOffRequestDialog, setShowTimeOffRequestDialog] = useState(false);
  const [requestToEdit, setRequestToEdit] = useState(null);

  const CELL_STYLES = {
    fontFamily: 'proxima-soft, sans-seriff',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: 1.5,
    color: colors.brand_4
  };

  const theme = createMuiTheme({
    // overrides for the approve/deny icon colors
    palette: {
      primary: {
        main: colors.brand_1
      },
      secondary: {
        main: colors.success
      },
      error: {
        main: colors.danger
      }
    },
    typography: {
      // override title prop font styling
      h6: {
        fontFamily: 'proxima-soft, sans-seriff',
        color: colors.brand_4
      },
      // override MTableGroupbar font styling
      caption: {
        fontFamily: 'proxima-soft, sans-seriff',
        fontSize: 10,
        fontWeight: 500,
        color: colors.brand_4
      }
    },
    overrides: {
      MuiCheckbox: {
        colorSecondary: {
          '&$checked': {
            color: colors.brand_3
          }
        }
      }
    }
  });

  const inThePast = request => moment(request.start).isBefore(moment());

  // a time off request is invalid if it's in a pending state AND the start date is before "today"; aka it's an old request that was never addressed
  const isInvalid = request => !request.status && inThePast(request);

  /**
   * sort data for `<MaterialTable />`
   * - pending requests are shown, then approved, then denied
   * - in each sub group, then sort by `createdAt` in descending order so oldest requests are given preference over newer requests
   */
  const sorter = data => orderBy(data, [
    element => {
      // custom ranking to ensure pending requests are shown first
      const rank = {
        'pending': 1,
        'approve': 2,
        'reject': 3
      };

      // if a request was never approved or denied and has passed, show after the rejected group
      if (isInvalid(element)) {
        return 4;
      }

      return rank[element['requestStatus']];
    },
    element => element.createdAt
  ], ['asc', 'asc']);

  const sortedData = sorter(timeOffRequests.map(request => {
    const { employeeId } = request;

    // add employee name and request status so that material-table can use these values
    return {
      ...request,
      employeeName: employeesById[employeeId] ? employeesById[employeeId].name : '',
      requestStatus: request.status ? request.status.status : isInvalid(request) ? 'invalid' : 'pending'
    };
  }));

  // add additional styling to the rows
  const applyRowStyle = rowData => {
    let styles = {
      border: '1px solid #F1F1F3',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    };

    if (rowData.status) {
      const { status } = rowData.status;
      const rowColor = status === 'approve' ? colors.success_background : colors.danger_background;

      styles['backgroundColor'] = rowColor;
    }

    if (isInvalid(rowData)) {
      styles['backgroundColor'] = colors.athensGray2;
    }

    return styles;
  };

  const lessThanTwoWeekNotice = (startDate, createdDate) => {
    const momentStart = moment(startDate);
    const momentCreatedAt = moment(createdDate);

    return momentStart.isBetween(momentCreatedAt, momentCreatedAt.clone().add(2, 'weeks'));
  };

  // show `START - END` for 'Requested Date' column
  const formatTimeframe = rowData => {
    const isSameDay = moment(rowData.start).isSame(moment(rowData.end), 'day');

    if (rowData.allDay && isSameDay) {
      return 'All Day';
    } else if (isSameDay) {
      return `${moment(rowData.start).format('h:mm a')} - ${moment(rowData.end).format('h:mm a')}`;
    } else {
      return `${moment(rowData.start).format('M-DD-YYYY')} - ${moment(rowData.end).format('M-DD-YYYY')}`;
    }
  };
  // format 'createdAt' to Month-Day-Year
  const formatDateReceived = rowData => {
    const lessThanTwoWeeks = lessThanTwoWeekNotice(rowData.start, rowData.createdAt);

    return <span style={{ color: lessThanTwoWeeks ? colors.danger : colors.brand_4 }}>{moment(rowData.createdAt).format('M-DD-YYYY')}</span>;
  };
  // format `start`
  const formatDateRequested = rowData => moment(rowData.start).format('M-DD-YYYY');
  // format `requestStatus`
  const formatStatus = rowData => {
    // if the 'status' column is used for grouping, `rowData` will be a string representing the status, otherwise `rowData` will be an object representing the time off request
    const value = typeof rowData === 'string' ? rowData : rowData.requestStatus;

    switch (value) {
      case 'approve': return 'Approved';
      case 'reject': return 'Denied';
      case 'invalid': return 'Invalid';
      default: return 'Pending';
    }
  };

  const renderStatus = rowData => {
    return formatStatus(rowData);
  };

  const renderPTOHours = rowData => {
    const isUnpaid = rowData.type.match(/unpaid/i);
    const status = formatStatus(rowData);
    const showApprovedHours = status.match(/Approved/);
    const stateMatchesRow = editsToExistingRequest.id === rowData.id;
    let inputValue = '0.00';

    if (!isUnpaid) {
      if (rowData.status) {
        inputValue = stateMatchesRow ? editsToExistingRequest.hoursApproved : rowData.status.hoursApproved;
      }
    }

    let hasError = false;

    if (stateMatchesRow) {
      // ensure the new value is not larger than the number of hours requested
      const hoursRequested = numberOfHoursRequested(rowData);
      const invalidHours = parseFloat(editsToExistingRequest.hoursApproved, 10) > parseFloat(hoursRequested);
      const emptyInput = editsToExistingRequest.hoursApproved === '';
      hasError = invalidHours || emptyInput;
    }

    return (showApprovedHours && rowData.status &&
      <div className='flex-rows' style={{ alignItems: 'center' }}>
        <TextInput
          disabled={isUnpaid}
          key={`textinput-${rowData.id}`}
          autoFocus={stateMatchesRow}
          style={{ minWidth: 72 }}
          value={inputValue}
          onChange={event => setEditsToExistingRequest({ id: rowData.id, hoursApproved: event.target.value })}
          type='number'
          pattern={/^\d+(\.\d{1,2})?$/}
          hasError={hasError}
          errorText='Cannot be empty or exceed length of request'
        />
        { stateMatchesRow && !hasError &&
          <i
            className='fa fa-save'
            style={{ cursor: 'pointer', margin: '8px 0 0 8px', color: '#8E9BAC' }}
            onClick={() => {
              // dispatch saga
              updateTimeOffRequestStatus([{
                id: editsToExistingRequest.id,
                hoursApproved: parseFloat(editsToExistingRequest.hoursApproved).toFixed(2),
                status: 'approve'
              }]);
              // let the request execute before reseting the state
              setTimeout(() => setEditsToExistingRequest({ id: null, hoursApproved: null }), 1500);
            }}
          />
        }
      </div>
    );
  };

  // get the number of hours requested
  const numberOfHoursRequested = rowData => {
    const isSameDay = moment(rowData.start).isSame(moment(rowData.end), 'day');

    // all day requests that are same day are simply 8 hours
    if (rowData.allDay && isSameDay) {
      return parseFloat(8).toFixed(2);
    } else if (!isSameDay) {
      // all day requests that aren't the same date are a range of dates
      // only use working days to calculate the timeframe of the request
      const dayDiff = numberOfWorkingDays(rowData.start, rowData.end);
      return parseFloat(dayDiff * 8.00).toFixed(2);
    } else {
      return parseFloat(moment(rowData.end).diff(moment(rowData.start), 'seconds') / 3600).toFixed(2);
    }
  };

  const exportToCsv = (columns, data) => {
    let formattedColumns = columns.map(col => {
      // don't include the PTO Hour column in the csv since it's broken into approved pto and approved unpaid
      return col.title.match(/PTO\sHours/) ? undefined : col.title;
    }).filter(i => i !== undefined);
    formattedColumns.push('Approved PTO');
    formattedColumns.push('Approved Unpaid');
    let dataToExport = data;

    // when grouped, data is an array of the groups where each object has an `isExpanded` and `data` property
    const dataIsGrouped = data.every(d => d.isExpanded !== undefined);

    if (dataIsGrouped) {
      const expandedData = flatten(data.filter(d => d.isExpanded).map(g => g.data));

      // don't create an empty export
      if (expandedData.length === 0) {
        return;
      }

      dataToExport = expandedData;
    }

    const formattedRows = dataToExport.map(row => {
      const { employeeName, createdAt, description } = row;
      const timeframe = formatTimeframe(row);
      const requested = formatDateRequested(row);
      const received = moment(createdAt).format('M-DD-YYYY');
      const status = formatStatus(row);
      let approvedPTO = 0; let approvedUnpaid = 0;

      if (status.match(/Approved/)) {
        if (row.type.match(/PTO/)) {
          approvedPTO = row.status.hoursApproved;
          approvedUnpaid = parseFloat(parseFloat(numberOfHoursRequested(row)) - parseFloat(approvedPTO));
        } else {
          approvedPTO = 0;
          approvedUnpaid = row.status.hoursApproved;
        }
      }

      return [employeeName, received, requested, timeframe, description, status, parseFloat(approvedPTO).toFixed(2), parseFloat(approvedUnpaid).toFixed(2)];
    });

    // CsvBuilder expects rows to be an array of string
    const builder = new CsvBuilder('time_off_requests.csv');
    builder.setDelimeter(',')
      .setColumns(formattedColumns)
      .addRows(formattedRows)
      .exportFile();
  };

  /**
   * Determine if a table action (approve or reject) should be hidden
   *
   * @param {Object[]} rowData
   * @param {String} action
   * @returns {Boolean}
   */
  const actionIsHidden = (rowData, action) => {
    if (rowData.length > 1) {
      let hideAction = false;
      let requestStatus = '';
      // ensure all items have the same requestStatus
      for (let index = 0; index < rowData.length; index++) {
        const element = rowData[index];

        if (index === 0) {
          requestStatus = element.requestStatus;
          continue;
        } else if (element.requestStatus !== requestStatus) {
          hideAction = true;
          break;
        }
      }

      return hideAction;
    } else if (rowData.length === 1) {
      const row = rowData[0];

      if (isInvalid(row)) return true;

      if (row.status) {
        if (row.requestStatus.match(new RegExp(`${action}`))) return true;
        if (row.requestStatus.match(/(approve|reject)/) && inThePast(row)) return true;
      }
    }

    return false;
  };

  /**
   * make request to update the requested time off request(s)
   * @param {Object[]} rowData
   * @param {String} action
   */
  const performRequestUpdates = (rowData, status) => {
    let payload = [];
    rowData.forEach(req => {
      const hoursApproved = status.match(/reject/i) ? '0.00' : numberOfHoursRequested(req);

      payload.push({
        id: req.id,
        status,
        hoursApproved
      });
    });

    updateTimeOffRequestStatus(payload);
  };

  return (
    <Fragment>
      <div className='flex' style={{ margin: '8px 0', justifyContent: 'flex-end' }}>
        <div style={{ margin: '0 8px' }}>
          <TextInput
            type='date'
            label='Start'
            value={searchTimeframeStart && moment(searchTimeframeStart).format('YYYY-MM-DD')}
            onChange={event => setTimeOffRequestSearchTimeframe(moment(event.target.value), 'timeOffRequestTimeframeStart')}
          />
        </div>
        <TextInput
          type='date'
          label='End'
          value={searchTimeframeEnd && moment(searchTimeframeEnd).format('YYYY-MM-DD')}
          onChange={event => setTimeOffRequestSearchTimeframe(moment(event.target.value), 'timeOffRequestTimeframeEnd')}
        />
        <div className='flex-columns' style={{ justifyContent: 'flex-end' }}>
          <Button
            onClick={searchForRequestsInTimeframe}
            disabled={!searchTimeframeStart || !searchTimeframeEnd || moment(searchTimeframeStart).isAfter(moment(searchTimeframeEnd))}
          >
            Search
          </Button>
        </div>
      </div>
      <MuiThemeProvider theme={theme}>
        <StylesProvider generateClassName={generateClassName}>
          <MaterialTable
            title='Time Off'
            columns={[
              { title: 'Employee', field: 'employeeName', cellStyle: CELL_STYLES },
              { title: 'Received', field: 'createdAt', render: formatDateReceived, cellStyle: CELL_STYLES, grouping: false },
              { title: 'Requested', field: 'start', cellStyle: CELL_STYLES, render: formatDateRequested, grouping: false },
              {
                title: 'Timeframe',
                render: (rowData) => (
                  <div className='flex-columns'>
                    { formatTimeframe(rowData) }
                    <span>({numberOfHoursRequested(rowData)} hrs)</span>
                  </div>
                ),
                cellStyle: CELL_STYLES,
                sorting: false,
                grouping: false
              },
              { title: 'Description', field: 'description', cellStyle: CELL_STYLES, sorting: false, grouping: false },
              {
                title: 'Status',
                field: 'requestStatus',
                render: renderStatus,
                searchable: true,
                cellStyle: rowData => ({
                  ...CELL_STYLES,
                  fontWeight: 600,
                  fontSize: 12,
                  color: rowData === 'approve' ? colors.success : rowData === 'reject' ? colors.danger : colors.brand_4
                })
              },
              {
                title: 'PTO Hours',
                field: 'ptoHours',
                render: renderPTOHours,
                cellStyle: CELL_STYLES,
                sorting: false,
                grouping: false
              }
            ]}
            data={sortedData}
            isLoading={isLoading}
            components={{
              Container: props => <Paper {...props} elevation={0} />
            }}
            style={{
              border: '1px solid #F1F1F3'
            }}
            options={{
              sorting: true,
              selection: true,
              search: true,
              grouping: true,
              exportButton: true,
              exportCsv: exportToCsv,
              pageSize: 10,
              actionsColumnIndex: -1,
              rowStyle: applyRowStyle,
              headerStyle: {
                fontFamily: 'proxima-soft, sans-seriff',
                fontWeight: 600,
                fontSize: 14,
                color: colors.brand_4,
                backgroundColor: '#F1F1F3'
              }
            }}
            actions={[
              rowData => {
                return ({
                  icon: 'check',
                  tooltip: 'Approve',
                  iconProps: { color: 'secondary' },
                  hidden: actionIsHidden(rowData, 'approve'),
                  onClick: (event, rowData) => {
                    if (rowData.length) {
                      performRequestUpdates(rowData, 'approve');
                    }
                  }
                });
              },
              rowData => {
                return ({
                  icon: 'clear',
                  tooltip: 'Deny',
                  iconProps: { color: 'error' },
                  hidden: actionIsHidden(rowData, 'reject'),
                  onClick: (event, rowData) => {
                    if (rowData.length) {
                      performRequestUpdates(rowData, 'reject');
                    }
                  }
                });
              },
              rowData => {
                const isHidden = (rowData.length > 1 || (rowData.length === 1 && ((rowData[0].status && !rowData[0].requestStatus.match(/(pending|approve|reject)/)) || inThePast(rowData[0]))));

                return ({
                  icon: 'edit',
                  tooltip: 'Edit',
                  hidden: isHidden,
                  onClick: (event, rowData) => {
                    if (rowData.length > 1) {
                      return;
                    }

                    const row = rowData[0];

                    setRequestToEdit(row);
                    setShowTimeOffRequestDialog(true);
                  }
                });
              },
              rowData => {
                return ({
                  icon: 'delete',
                  tooltip: 'Archive',
                  onClick: (event, rowData) => archiveRequest(rowData.map(row => row.id))
                });
              },
              {
                icon: 'add',
                tooltip: 'New Request',
                isFreeAction: true,
                hidden: false,
                onClick: showManagerEmployeeTimeOffRequestDialog
              }
            ]}
            localization={{
              grouping: {
                placeholder: 'Group by Employee or Status'
              }
            }}
          />
          { showTimeOffRequestDialog &&
            <TimeOffRequestDialog
              isOpen={showTimeOffRequestDialog}
              onClose={() => setShowTimeOffRequestDialog(false)}
              request={requestToEdit}
              makingRequest={false}
              timeOffRequestAlert={null}
              currentCenterId={currentCenterId}
              onSubmit={(requestType, timeOffType, start, end, description) => {
                editRequest(requestToEdit.id, requestType, timeOffType, start, end, description);
                setShowTimeOffRequestDialog(false);
              }}
            />
          }
        </StylesProvider>
      </MuiThemeProvider>
    </Fragment>
  );
};

TimeOffOverview.propTypes = {
  employeesById: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      id: PropTypes.number,
      image: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string
    })
  }),
  timeOffRequests: PropTypes.arrayOf(PropTypes.shape({
    allDay: PropTypes.bool,
    createdAt: PropTypes.string,
    description: PropTypes.string,
    employeeId: PropTypes.number,
    end: PropTypes.string,
    id: PropTypes.number,
    start: PropTypes.string,
    status: PropTypes.shape({
      createdAt: PropTypes.string,
      createdById: PropTypes.number,
      status: PropTypes.string,
      statusId: PropTypes.number
    })
  })),
  currentCenterId: PropTypes.number,
  updateTimeOffRequestStatus: PropTypes.func,
  editRequest: PropTypes.func, // calls saga with updated information
  archiveRequest: PropTypes.func, // calls archive saga
  searchForRequestsInTimeframe: PropTypes.func, // calls saga to fetch time off requests
  searchTimeframeStart: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  searchTimeframeEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  setTimeOffRequestSearchTimeframe: PropTypes.func, // set a date for the query filter
  showManagerEmployeeTimeOffRequestDialog: PropTypes.func // toggel the dialog for a manager to create a time off request on behalf of an employee
};

export default TimeOffOverview;
